import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const PaymentSuccess = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [paymentData, setPaymentData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Récupérer les paramètres de retour de CyberSource
    const urlParams = new URLSearchParams(location.search);
    const paymentInfo = {
      decision: urlParams.get('decision'),
      reason_code: urlParams.get('reason_code'),
      message: urlParams.get('message'),
      transaction_id: urlParams.get('transaction_id'),
      auth_code: urlParams.get('auth_code'),
      amount: urlParams.get('req_amount'),
      currency: urlParams.get('req_currency'),
      reference_number: urlParams.get('req_reference_number'),
      auth_trans_ref_no: urlParams.get('auth_trans_ref_no'),
      bill_to_forename: urlParams.get('req_bill_to_forename'),
      bill_to_surname: urlParams.get('req_bill_to_surname'),
      bill_to_email: urlParams.get('req_bill_to_email'),
      card_type_name: urlParams.get('card_type_name'),
      auth_time: urlParams.get('auth_time'),
      auth_reconciliation_reference_number: urlParams.get('auth_reconciliation_reference_number')
    };
    
    setPaymentData(paymentInfo);
    setLoading(false);
  }, [location]);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f8f9fa'
      }}>
        <div style={{
          textAlign: 'center',
          padding: '40px',
          backgroundColor: 'white',
          borderRadius: '15px',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)'
        }}>
          <Icon icon="eos-icons:loading" style={{ fontSize: '48px', color: '#28a745' }} />
          <p style={{ marginTop: '20px', fontSize: '18px', color: '#666' }}>
            {t('subscription.payment.result.processing')}
          </p>
        </div>
      </div>
    );
  }

  const isSuccess = paymentData?.decision === 'ACCEPT';

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#C6E3EF',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '25px',
        padding: '50px',
        maxWidth: '700px',
        width: '100%',
        textAlign: 'center',
        boxShadow: '0 25px 50px rgba(0, 0, 0, 0.2)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Décoration de fond */}
        <div style={{
          position: 'absolute',
          top: '-50px',
          right: '-50px',
          width: '200px',
          height: '200px',
          background: 'linear-gradient(45deg, #28a745, #20c997)',
          borderRadius: '50%',
          opacity: 0.1
        }}></div>
        <div style={{
          position: 'absolute',
          bottom: '-30px',
          left: '-30px',
          width: '150px',
          height: '150px',
          background: 'linear-gradient(45deg, #007bff, #6610f2)',
          borderRadius: '50%',
          opacity: 0.1
        }}></div>
        {/* Icône de statut */}
        <div style={{
          width: '120px',
          height: '120px',
          borderRadius: '50%',
          background: isSuccess 
            ? 'linear-gradient(135deg, #28a745, #20c997)' 
            : 'linear-gradient(135deg, #dc3545, #e74c3c)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 30px',
          boxShadow: isSuccess 
            ? '0 15px 30px rgba(40, 167, 69, 0.3)' 
            : '0 15px 30px rgba(220, 53, 69, 0.3)',
          position: 'relative',
          zIndex: 1
        }}>
          <Icon 
            icon={isSuccess ? "material-symbols:check-circle" : "material-symbols:error"} 
            style={{ 
              fontSize: '60px', 
              color: 'white',
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
            }} 
          />
        </div>

        {/* Titre */}
        <h1 style={{
          background: isSuccess 
            ? 'linear-gradient(135deg, #28a745, #20c997)' 
            : 'linear-gradient(135deg, #dc3545, #e74c3c)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          fontSize: '36px',
          fontWeight: 'bold',
          marginBottom: '20px',
          position: 'relative',
          zIndex: 1
        }}>
          {isSuccess ? t('subscription.payment.result.successTitle') : t('subscription.payment.result.failureTitle')}
        </h1>

        {/* Message */}
        <p style={{
          fontSize: '20px',
          color: '#555',
          marginBottom: '30px',
          lineHeight: '1.6',
          position: 'relative',
          zIndex: 1
        }}>
          {isSuccess 
            ? t('subscription.payment.result.successMsg')
            : paymentData?.message || t('subscription.payment.result.defaultError')
          }
        </p>

        {/* Message de remerciement pour le succès */}
        {isSuccess && (
          <div style={{
            backgroundColor: '#e8f5e8',
            border: '2px solid #28a745',
            borderRadius: '15px',
            padding: '20px',
            marginBottom: '30px',
            position: 'relative',
            zIndex: 1
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '10px' }}>
              <Icon icon="material-symbols:celebration" style={{ color: '#28a745', fontSize: '24px' }} />
              <span style={{ fontWeight: 'bold', color: '#28a745', fontSize: '18px' }}>
                {t('subscription.payment.result.thanks')}
              </span>
            </div>
            <p style={{ margin: 0, color: '#555', fontSize: '16px' }}>
              {t('subscription.payment.result.secured')}
            </p>
          </div>
        )}

        {/* Détails du paiement */}
        {paymentData && (
          <div style={{
            background: 'linear-gradient(135deg, #f8f9fa, #e9ecef)',
            padding: '25px',
            borderRadius: '15px',
            marginBottom: '30px',
            textAlign: 'left',
            border: '1px solid #dee2e6',
            position: 'relative',
            zIndex: 1
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
              <Icon icon="material-symbols:receipt" style={{ color: '#007bff', fontSize: '24px' }} />
              <h3 style={{ color: '#333', margin: 0, fontSize: '20px', fontWeight: 'bold' }}>
                {t('subscription.payment.result.details')}
              </h3>
            </div>
            
            <div style={{ display: 'grid', gap: '15px' }}>
              {/* Statut avec badge */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '10px',
                backgroundColor: isSuccess ? '#d4edda' : '#f8d7da',
                borderRadius: '8px',
                border: `1px solid ${isSuccess ? '#c3e6cb' : '#f5c6cb'}`
              }}>
                <span style={{ fontWeight: '600', color: '#333' }}>{t('subscription.payment.result.status')}</span>
                <span style={{ 
                  color: isSuccess ? '#155724' : '#721c24',
                  fontWeight: 'bold',
                  padding: '5px 12px',
                  borderRadius: '20px',
                  backgroundColor: isSuccess ? '#28a745' : '#dc3545',
                  color: 'white',
                  fontSize: '14px'
                }}>
                  {isSuccess ? t('subscription.payment.result.accepted') : t('subscription.payment.result.refused')}
                </span>
              </div>

              {/* Montant en évidence */}
              {paymentData.amount && (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding: '15px',
                  backgroundColor: '#e3f2fd',
                  borderRadius: '10px',
                  border: '2px solid #bbdefb'
                }}>
                  <span style={{ fontWeight: '600', color: '#333', fontSize: '16px' }}>{t('subscription.payment.result.amount')}</span>
                  <span style={{ 
                    fontWeight: 'bold', 
                    fontSize: '20px',
                    color: '#1976d2'
                  }}>
                    {paymentData.amount} {paymentData.currency || 'USD'}
                  </span>
                </div>
              )}

              {/* Informations client */}
              {(paymentData.bill_to_forename || paymentData.bill_to_surname) && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: '500' }}>{t('subscription.payment.result.customer')}</span>
                  <span>{paymentData.bill_to_forename} {paymentData.bill_to_surname}</span>
                </div>
              )}

              {/* Référence */}
              {paymentData.reference_number && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: '500' }}>{t('subscription.payment.result.reference')}</span>
                  <span style={{ fontFamily: 'monospace', color: '#666' }}>{paymentData.reference_number}</span>
                </div>
              )}

              {/* Type de carte */}
              {paymentData.card_type_name && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: '500' }}>{t('subscription.payment.result.card')}</span>
                  <span style={{ color: '#666' }}>{paymentData.card_type_name}</span>
                </div>
              )}

              {/* Code d'autorisation */}
              {paymentData.auth_code && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: '500' }}>{t('subscription.payment.result.authCode')}</span>
                  <span style={{ fontFamily: 'monospace', color: '#666' }}>{paymentData.auth_code}</span>
                </div>
              )}

              {/* ID Transaction */}
              {paymentData.transaction_id && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: '500' }}>{t('subscription.payment.result.transactionId')}</span>
                  <span style={{ fontFamily: 'monospace', fontSize: '12px', color: '#666' }}>
                    {paymentData.transaction_id}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Boutons d'action */}
        <div style={{
          display: 'flex',
          gap: '20px',
          justifyContent: 'center',
          flexWrap: 'wrap',
          position: 'relative',
          zIndex: 1
        }}>
          <button
            onClick={() => window.location.href = '/'}
            style={{
              background: 'linear-gradient(135deg, #28a745, #20c997)',
              color: 'white',
              border: 'none',
              borderRadius: '30px',
              padding: '15px 35px',
              fontSize: '18px',
              fontWeight: 'bold',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              boxShadow: '0 8px 20px rgba(40, 167, 69, 0.3)',
              minWidth: '200px',
              justifyContent: 'center'
            }}
            onMouseOver={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 12px 25px rgba(40, 167, 69, 0.4)';
            }}
            onMouseOut={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 8px 20px rgba(40, 167, 69, 0.3)';
            }}
          >
            <Icon icon="material-symbols:home" style={{ fontSize: '20px' }} />
            {t('subscription.payment.result.home')}
          </button>

          {!isSuccess && (
            <button
              onClick={() => window.history.back()}
              style={{
                background: 'linear-gradient(135deg, #007bff, #6610f2)',
                color: 'white',
                border: 'none',
                borderRadius: '30px',
                padding: '15px 35px',
                fontSize: '18px',
                fontWeight: 'bold',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                boxShadow: '0 8px 20px rgba(0, 123, 255, 0.3)',
                minWidth: '200px',
                justifyContent: 'center'
              }}
              onMouseOver={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 12px 25px rgba(0, 123, 255, 0.4)';
              }}
              onMouseOut={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 8px 20px rgba(0, 123, 255, 0.3)';
              }}
            >
              <Icon icon="material-symbols:refresh" style={{ fontSize: '20px' }} />
              {t('subscription.payment.result.retry')}
            </button>
          )}
        </div>

        {/* Informations de contact */}
        <div style={{
          marginTop: '40px',
          padding: '25px',
          background: 'linear-gradient(135deg, #e3f2fd, #f3e5f5)',
          borderRadius: '15px',
          fontSize: '16px',
          color: '#555',
          border: '1px solid #bbdefb',
          position: 'relative',
          zIndex: 1
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
            <Icon icon="material-symbols:support-agent" style={{ color: '#1976d2', fontSize: '24px' }} />
            <strong style={{ color: '#333', fontSize: '18px' }}>{t('subscription.payment.result.needHelp')}</strong>
          </div>
          <p style={{ margin: 0, marginBottom: '10px', fontSize: '16px' }}>
            {t('subscription.payment.result.helpText')}
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Icon icon="material-symbols:phone" style={{ color: '#28a745', fontSize: '18px' }} />
              <span style={{ fontWeight: '500' }}>+221 33 824 33 45</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Icon icon="material-symbols:email" style={{ color: '#007bff', fontSize: '18px' }} />
              <span style={{ fontWeight: '500' }}>diaspora@transvie.sn</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
